import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gauge, Paperclip, Image as ImageIcon, X, Loader2, Save } from "lucide-react";
import { apiFetch, apiUpload } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BackHeader } from "@/components/common";
import { PlaceSearch } from "@/components/PlaceSearch";

const PALLET_FORMATS = [["italia", "Italia 100×120"], ["eur", "EUR/EPAL 80×120"], ["altro", "Altro"]];

export default function PublishTripPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    pickup: "", delivery: "", date: "", load_from: "", load_to: "", load_notes: "",
    unload_date: "", unload_from: "", unload_to: "", unload_notes: "",
    cargo: "", temperature: "", pallets: "", weight: "", vehicle_type: "", pallet_format: "",
    payload_capacity: "", length: "", price_proposed: "", notes: "",
  });
  const [pickupPlace, setPickupPlace] = useState(null);
  const [deliveryPlace, setDeliveryPlace] = useState(null);
  const [requirements] = useState([]);
  const [visibility, setVisibility] = useState("public");
  const [timerMinutes, setTimerMinutes] = useState(15);
  const [estimate, setEstimate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const pickup = form.pickup.trim();
    const delivery = form.delivery.trim();
    if (!pickup || !delivery) { setEstimate(null); return; }
    const t = setTimeout(() => {
      apiFetch(`/estimate?pickup=${encodeURIComponent(pickup)}&delivery=${encodeURIComponent(delivery)}`)
        .then(setEstimate).catch(() => setEstimate(null));
    }, 600);
    return () => clearTimeout(t);
  }, [form.pickup, form.delivery]);

  const set = (key, value) => setForm((old) => ({ ...old, [key]: value }));
  const showTemperature = form.vehicle_type.toLowerCase().includes("frig");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const doc = await apiUpload("/upload", file);
      setAttachments((old) => [...old, doc]);
    } catch (error) {
      setFormError(error.message || "Caricamento non riuscito.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const buildBody = (draft) => JSON.stringify({
    ...form,
    pickup_place: pickupPlace,
    delivery_place: deliveryPlace,
    pallets: form.pallets ? Number(form.pallets) : null,
    temperature: showTemperature ? (form.temperature || null) : null,
    price_proposed: form.price_proposed ? Number(form.price_proposed) : null,
    pallet_format: form.pallet_format || null,
    requirements,
    visibility,
    timer_minutes: visibility === "carriers_first" ? timerMinutes : null,
    payload_capacity: form.payload_capacity || null,
    length: form.length || null,
    attachments: attachments.map((a) => a.id),
    draft,
  });

  const submit = async (draft) => {
    if (!form.pickup.trim() || !form.delivery.trim() || !form.date.trim() || !form.vehicle_type.trim()) {
      setFormError("Inserisci almeno carico, scarico, data di carico e mezzo richiesto.");
      return;
    }
    setFormError("");
    if (draft) setSavingDraft(true); else setSaving(true);
    try {
      const created = await apiFetch("/trips", { method: "POST", body: buildBody(draft) });
      if (draft) navigate("/transports", { replace: true });
      else navigate(`/trip/${created.id}`, { replace: true });
    } catch (error) {
      setFormError(error.message || "Impossibile salvare. Riprova.");
    } finally {
      setSaving(false);
      setSavingDraft(false);
    }
  };

  const inputCls = "min-h-[50px] w-full rounded-xl border border-border bg-card px-3 text-[15px] outline-none focus:border-primary";
  const label = "mt-2 block text-xs font-bold text-card-foreground";
  const section = "mt-6 text-lg font-extrabold text-foreground";
  const chip = (active) =>
    `rounded-full px-3.5 py-2.5 text-xs font-bold ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`;

  return (
    <div className="mx-auto max-w-2xl">
      <BackHeader label="Pubblica viaggio" />
      <h1 className="text-2xl font-extrabold text-foreground">Nuovo viaggio</h1>
      <p className="mt-1 text-sm text-muted-foreground">Inserisci i dettagli essenziali. Potrai completarli in seguito.</p>

      <h2 className={section}>Punto di carico</h2>
      <label className={label}>Luogo di carico</label>
      <PlaceSearch testId="publish-field-pickup" value={form.pickup} onChange={(v) => set("pickup", v)} onSelectPlace={setPickupPlace} selected={pickupPlace} placeholder="Cerca deposito, azienda o indirizzo" />
      <label className={label}>Data di carico</label>
      <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="publish-field-date" />
      <div className="mt-1 flex gap-2.5">
        <div className="flex-1"><label className={label}>Disponibile dalle</label><input type="time" value={form.load_from} onChange={(e) => set("load_from", e.target.value)} className={inputCls} data-testid="publish-field-load-from" /></div>
        <div className="flex-1"><label className={label}>Disponibile fino alle</label><input type="time" value={form.load_to} onChange={(e) => set("load_to", e.target.value)} className={inputCls} data-testid="publish-field-load-to" /></div>
      </div>
      <label className={label}>Note carico</label>
      <input value={form.load_notes} onChange={(e) => set("load_notes", e.target.value)} placeholder="Es. presentarsi alla reception" className={inputCls} data-testid="publish-field-load-notes" />
      <p className="mt-1 text-[11px] text-muted-foreground">Le fasce orarie sono informazioni operative dello stabilimento. Il viaggio scade in base alla sola data di carico: resta disponibile per tutto il {form.date || "giorno indicato"}, poi diventa automaticamente “Scaduto”.</p>

      <h2 className={section}>Punto di scarico</h2>
      <label className={label}>Luogo di scarico</label>
      <PlaceSearch testId="publish-field-delivery" value={form.delivery} onChange={(v) => set("delivery", v)} onSelectPlace={setDeliveryPlace} selected={deliveryPlace} placeholder="Cerca deposito, azienda o indirizzo" />
      <label className={label}>Data di scarico</label>
      <input type="date" value={form.unload_date} onChange={(e) => set("unload_date", e.target.value)} className={inputCls} data-testid="publish-field-unload-date" />
      <div className="mt-1 flex gap-2.5">
        <div className="flex-1"><label className={label}>Disponibile dalle</label><input type="time" value={form.unload_from} onChange={(e) => set("unload_from", e.target.value)} className={inputCls} data-testid="publish-field-unload-from" /></div>
        <div className="flex-1"><label className={label}>Disponibile fino alle</label><input type="time" value={form.unload_to} onChange={(e) => set("unload_to", e.target.value)} className={inputCls} data-testid="publish-field-unload-to" /></div>
      </div>
      <label className={label}>Note scarico</label>
      <input value={form.unload_notes} onChange={(e) => set("unload_notes", e.target.value)} placeholder="Es. scarico su rampa" className={inputCls} data-testid="publish-field-unload-notes" />

      {estimate?.km ? (
        <div data-testid="estimate-card" className="mt-3 flex items-center gap-3 rounded-xl bg-secondary p-3 text-secondary-foreground">
          <Gauge className="h-5 w-5" />
          <div>
            <p className="text-sm font-extrabold">Distanza stimata: ~{estimate.km} km</p>
            <p className="text-xs font-semibold">Costo indicativo del trasporto: ~€ {estimate.cost}</p>
          </div>
        </div>
      ) : null}

      <h2 className={section}>Carico e mezzo</h2>
      <label className={label}>Tipo di merce</label>
      <input value={form.cargo} onChange={(e) => set("cargo", e.target.value)} placeholder="Es. Prodotti alimentari" className={inputCls} />
      <label className={label}>Formato pallet</label>
      <div className="mt-1 flex flex-wrap gap-2">
        {PALLET_FORMATS.map(([val, lbl]) => (
          <button key={val} type="button" data-testid={`pallet-format-${val}`} onClick={() => set("pallet_format", form.pallet_format === val ? "" : val)} className={chip(form.pallet_format === val)}>{lbl}</button>
        ))}
      </div>
      <div className="mt-1 flex gap-2.5">
        <div className="flex-1"><label className={label}>Pallet</label><input value={form.pallets} onChange={(e) => set("pallets", e.target.value)} placeholder="26" inputMode="numeric" className={inputCls} /></div>
        <div className="flex-1"><label className={label}>Peso</label><input value={form.weight} onChange={(e) => set("weight", e.target.value)} placeholder="18.500 kg" className={inputCls} /></div>
      </div>
      <label className={label}>Mezzo richiesto</label>
      <input value={form.vehicle_type} onChange={(e) => set("vehicle_type", e.target.value)} placeholder="Es. Bilico frigorifero" className={inputCls} data-testid="publish-field-vehicle_type" />
      {showTemperature ? (
        <>
          <label className={label}>Temperatura richiesta</label>
          <input value={form.temperature} onChange={(e) => set("temperature", e.target.value)} placeholder="+2 / +8 °C" className={inputCls} data-testid="publish-field-temperature" />
        </>
      ) : null}
      <div className="mt-1 flex gap-2.5">
        <div className="flex-1"><label className={label}>Portata (kg)</label><input value={form.payload_capacity} onChange={(e) => set("payload_capacity", e.target.value)} placeholder="Es. 24.000" className={inputCls} /></div>
        <div className="flex-1"><label className={label}>Lunghezza</label><input value={form.length} onChange={(e) => set("length", e.target.value)} placeholder="Es. 13,60 m" className={inputCls} /></div>
      </div>

      <h2 className={section}>Offerta</h2>
      <label className={label}>Prezzo proposto</label>
      <input value={form.price_proposed} onChange={(e) => set("price_proposed", e.target.value)} placeholder="Es. 680" inputMode="numeric" className={inputCls} />
      <label className={label}>Note</label>
      <input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Istruzioni aggiuntive" className={inputCls} />

      <h2 className={section}>Allegati</h2>
      <div className="mt-1 flex gap-2.5">
        <label data-testid="attach-document-button" className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-border bg-card py-3 text-sm font-extrabold text-card-foreground">
          <Paperclip className="h-5 w-5 text-primary" /> Documento
          <input type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} />
        </label>
        <label data-testid="attach-photo-button" className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-border bg-card py-3 text-sm font-extrabold text-card-foreground">
          <ImageIcon className="h-5 w-5 text-primary" /> Foto
          <input type="file" accept="image/*" className="hidden" onChange={onFile} />
        </label>
      </div>
      {uploading ? (
        <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento in corso...</p>
      ) : null}
      {attachments.map((a) => (
        <div key={a.id} data-testid={`attachment-chip-${a.id}`} className="mt-2 flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5">
          <Paperclip className="h-4 w-4 text-primary" />
          <span className="flex-1 truncate text-[13px] font-bold text-foreground">{a.name}</span>
          <button data-testid={`attachment-remove-${a.id}`} onClick={() => setAttachments((old) => old.filter((e) => e.id !== a.id))}>
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      ))}

      {user?.account_type === "azienda" ? (
        <>
          <h2 className={section}>Visibilità</h2>
          <div className="mt-1 flex flex-wrap gap-2">
            <button data-testid="visibility-public" onClick={() => setVisibility("public")} className={chip(visibility === "public")}>Borsa generale</button>
            <button data-testid="visibility-carriers-first" onClick={() => setVisibility("carriers_first")} className={chip(visibility === "carriers_first")}>Prima i miei vettori</button>
            <button data-testid="visibility-carriers" onClick={() => setVisibility("carriers")} className={chip(visibility === "carriers")}>Solo i miei vettori</button>
          </div>
          {visibility === "carriers_first" ? (
            <>
              <label className={label}>Dopo quanto diventa pubblico</label>
              <div className="mt-1 flex flex-wrap gap-2">
                {[5, 10, 15, 30, 60].map((m) => (
                  <button key={m} data-testid={`timer-${m}`} onClick={() => setTimerMinutes(m)} className={chip(timerMinutes === m)}>{m} min</button>
                ))}
              </div>
            </>
          ) : null}
          <p className="mt-2 text-xs text-muted-foreground">
            {visibility === "public"
              ? "Il viaggio è subito visibile a tutti i vettori del mercato."
              : visibility === "carriers_first"
                ? `Riservato ai tuoi vettori per ${timerMinutes} minuti, poi passa in borsa generale.`
                : "Visibile solo ai vettori associati alla tua azienda."}
          </p>
        </>
      ) : null}

      {formError ? <p data-testid="publish-trip-error" className="mt-3 text-sm text-destructive">{formError}</p> : null}
      <div className="mt-6 flex flex-col gap-2 sm:flex-row">
        <button
          data-testid="save-draft-button"
          onClick={() => submit(true)}
          disabled={saving || savingDraft || uploading}
          className="flex min-h-[54px] flex-1 items-center justify-center gap-2 rounded-2xl border border-primary bg-card text-sm font-extrabold text-primary disabled:opacity-60"
        >
          {savingDraft ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
          {savingDraft ? "Salvataggio..." : "SALVA BOZZA"}
        </button>
        <button
          data-testid="publish-trip-submit"
          onClick={() => submit(false)}
          disabled={saving || savingDraft || uploading}
          className="flex min-h-[54px] flex-1 items-center justify-center gap-2 rounded-2xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
          {saving ? "Pubblicazione..." : "PUBBLICA VIAGGIO"}
        </button>
      </div>
    </div>
  );
}
